import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';

// 🚀 최종 교정: 대표님이 찾으신 완벽한 6MB짜리 한글 폰트 파일명으로 정확히 매핑
Font.register({
  family: 'NotoSansKR',
  fonts: [
    { src: '/fonts/NotoSansKR-Regular.ttf', fontWeight: 'normal' }, 
    { src: '/fonts/NotoSansKR-Bold.ttf', fontWeight: 'bold' }
  ]
});

const styles = StyleSheet.create({
  page: { padding: 25, fontFamily: 'NotoSansKR', fontSize: 7.5, color: '#000000' },
  
  mainTitle: { fontSize: 21, width: '100%', textAlign: 'center', marginBottom: 15, fontWeight: 'bold', paddingHorizontal: 10 },
  
  tableContainer: { borderWidth: 1, borderColor: '#000000' },
  dataRow: { flexDirection: 'row', height: 22 }, 
  headerRow: { flexDirection: 'row', height: 18 }, 
  
  cellCenter: { borderRightWidth: 1, borderBottomWidth: 1, borderColor: '#000000', justifyContent: 'center', alignItems: 'center' },
  cellLeft: { borderRightWidth: 1, borderBottomWidth: 1, borderColor: '#000000', justifyContent: 'center', paddingLeft: 5 },
  cellRight: { borderRightWidth: 1, borderBottomWidth: 1, borderColor: '#000000', justifyContent: 'center', alignItems: 'flex-end', paddingRight: 5 },
  
  textCenterBold: { width: '100%', textAlign: 'center', fontWeight: 'bold' },
  textCenter: { width: '100%', textAlign: 'center' },
  textLeft: { width: '100%', textAlign: 'left' },
  textRight: { width: '100%', textAlign: 'right' },
  textRightBold: { width: '100%', textAlign: 'right', fontWeight: 'bold' },

  legalSection: { paddingVertical: 15, alignItems: 'center', borderBottomWidth: 1, borderColor: '#000000' },
  legalText: { fontSize: 8, marginBottom: 8 },
  dateText: { fontSize: 9, marginBottom: 15, fontWeight: 'bold' },
  footerBox: { padding: 10, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end' },
  noticeList: { width: '65%', fontSize: 7, lineHeight: 1.4 },
  brandBox: { width: '35%', alignItems: 'flex-end', justifyContent: 'flex-end' },
  bankText: { fontSize: 8, fontWeight: 'bold', textAlign: 'right', marginBottom: 2 }
});

const AsInvoicePdf = ({ invoiceData }) => {
  if (!invoiceData) return null;

  const {
    carNumber = '', customerName = '', phone = '', address = '', 
    regDate = '', vin = '', mileage = '0', repairType = '일반',
    modelName = '', requestDate = '', completeDate = '', engineer = '',
    provider = {
      name: '주식회사 이투빌리티(이주희,김종배)', bizNum: '694-85-02998',
      regNum: '01-4118-000368', address: '경기도 하남시 동남로 385'
    },
    items = [],
    discount = 0
  } = invoiceData;

  const discountAmount = Number(discount) || 0;

  const vatRate = 0.1;
  let computedSubTotal = 0;
  
  const computedItems = items.map(item => {
    const qty = Number(item.qty || 0);
    const price = Number(item.price || 0);
    const labor = Number(item.labor || 0);
    const itemSum = (price * qty) + labor; 
    computedSubTotal += itemSum;
    return { ...item, type: item.type || 'A', qty, price, labor, calculatedSum: itemSum, note: item.note || '일반수리' };
  });

  const computedVat = Math.round(computedSubTotal * vatRate);
  const computedTotal = computedSubTotal + computedVat - discountAmount;

  const MAX_ROWS = 8;
  const blankRowsCount = Math.max(0, MAX_ROWS - computedItems.length);
  const blankRows = Array.from({ length: blankRowsCount });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.mainTitle}>자동차 점검·정비 명세서</Text>

        <View style={styles.tableContainer}>
          <View style={styles.dataRow}>
            <View style={[styles.cellCenter, { width: '5%', borderBottomWidth: 0, backgroundColor: '#f8fafc', justifyContent: 'flex-end', paddingBottom: 2 }]}><Text style={styles.textCenterBold}>차량</Text></View>
            <View style={[styles.cellCenter, { width: '13%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>차량번호</Text></View>
            <View style={[styles.cellCenter, { width: '22%' }]}><Text style={styles.textCenter}>{carNumber}</Text></View>
            <View style={[styles.cellCenter, { width: '15%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>차량 모델명(차종)</Text></View>
            <View style={[styles.cellCenter, { width: '15%' }]}><Text style={styles.textCenter}>{modelName}</Text></View>
            <View style={[styles.cellCenter, { width: '15%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>주행거리</Text></View>
            <View style={[styles.cellCenter, { width: '15%', borderRightWidth: 0 }]}><Text style={styles.textCenter}>{mileage}</Text></View>
          </View>

          <View style={styles.dataRow}>
            <View style={[styles.cellCenter, { width: '5%', backgroundColor: '#f8fafc', justifyContent: 'flex-start', paddingTop: 2 }]}><Text style={styles.textCenterBold}>소유자</Text></View>
            <View style={[styles.cellCenter, { width: '13%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>소유자</Text></View>
            <View style={[styles.cellCenter, { width: '22%' }]}><Text style={styles.textCenter}>{customerName}</Text></View>
            <View style={[styles.cellCenter, { width: '15%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>연락처</Text></View>
            <View style={[styles.cellCenter, { width: '15%' }]}><Text style={styles.textCenter}>{phone}</Text></View>
            <View style={[styles.cellCenter, { width: '15%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>주소</Text></View>
            <View style={[styles.cellCenter, { width: '15%', borderRightWidth: 0 }]}><Text style={styles.textCenter}>{address}</Text></View>
          </View>

          <View style={styles.dataRow}>
            <View style={[styles.cellCenter, { width: '18%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>최초등록일</Text></View>
            <View style={[styles.cellCenter, { width: '22%' }]}><Text style={styles.textCenter}>{regDate}</Text></View>
            <View style={[styles.cellCenter, { width: '15%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>차대번호</Text></View>
            <View style={[styles.cellCenter, { width: '15%' }]}><Text style={styles.textCenter}>{vin}</Text></View>
            <View style={[styles.cellCenter, { width: '15%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>점검정비 의뢰일자</Text></View>
            <View style={[styles.cellCenter, { width: '15%', borderRightWidth: 0 }]}><Text style={styles.textCenter}>{requestDate}</Text></View>
          </View>

          <View style={styles.dataRow}>
            <View style={[styles.cellCenter, { width: '5%', borderBottomWidth: 0, backgroundColor: '#f8fafc', justifyContent: 'flex-end', paddingBottom: 2 }]}><Text style={styles.textCenterBold}>공급자</Text></View>
            <View style={[styles.cellCenter, { width: '13%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>사업자등록번호</Text></View>
            <View style={[styles.cellCenter, { width: '22%' }]}><Text style={styles.textCenter}>{provider.bizNum}</Text></View>
            <View style={[styles.cellCenter, { width: '15%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>정비업등록번호</Text></View>
            <View style={[styles.cellCenter, { width: '15%' }]}><Text style={styles.textCenter}>{provider.regNum}</Text></View>
            <View style={[styles.cellCenter, { width: '15%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>구분(일반/AS)</Text></View>
            <View style={[styles.cellCenter, { width: '15%', borderRightWidth: 0 }]}><Text style={styles.textCenter}>{repairType}</Text></View>
          </View>

          <View style={styles.dataRow}>
            <View style={[styles.cellCenter, { width: '5%', backgroundColor: '#f8fafc' }]}><Text></Text></View>
            <View style={[styles.cellCenter, { width: '13%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>업체명(대표자)</Text></View>
            <View style={[styles.cellCenter, { width: '22%' }]}><Text style={styles.textCenter}>{provider.name}</Text></View>
            <View style={[styles.cellCenter, { width: '15%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>주소</Text></View>
            <View style={[styles.cellCenter, { width: '45%', borderRightWidth: 0 }]}><Text style={styles.textCenter}>{provider.address}</Text></View>
          </View>

          <View style={styles.dataRow}>
            <View style={[styles.cellCenter, { width: '18%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>점검정비 완료일자</Text></View>
            <View style={[styles.cellCenter, { width: '22%' }]}><Text style={styles.textCenter}>{completeDate}</Text></View>
            <View style={[styles.cellCenter, { width: '15%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>출고일자</Text></View>
            <View style={[styles.cellCenter, { width: '15%' }]}><Text style={styles.textCenter}>{completeDate}</Text></View>
            <View style={[styles.cellCenter, { width: '15%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenter}>정비 책임자</Text></View>
            <View style={[styles.cellCenter, { width: '15%', borderRightWidth: 0, flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 6 }]}>
               <Text>{engineer}</Text>
               <Text style={{color: '#000000', fontSize: 6}}>(서명)</Text>
            </View>
          </View>

          <View style={[styles.cellCenter, { borderRightWidth: 0, backgroundColor: '#f1f5f9', height: 22 }]}>
            <Text style={styles.textCenterBold}>점검 및 정비 내용</Text>
          </View>

          <View style={styles.headerRow}>
            <View style={[styles.cellCenter, { width: '4%', borderBottomWidth: 0, backgroundColor: '#f8fafc' }]}><Text style={styles.textCenterBold}>No</Text></View>
            <View style={[styles.cellCenter, { width: '33%', borderBottomWidth: 0, backgroundColor: '#f8fafc' }]}><Text style={styles.textCenterBold}>작업 내 용</Text></View>
            <View style={[styles.cellCenter, { width: '35%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenterBold}>사용 부품</Text></View>
            <View style={[styles.cellCenter, { width: '14%', borderBottomWidth: 0, backgroundColor: '#f8fafc' }]}><Text style={styles.textCenterBold}>기술료</Text></View>
            <View style={[styles.cellCenter, { width: '14%', borderRightWidth: 0, borderBottomWidth: 0, backgroundColor: '#f8fafc' }]}><Text style={styles.textCenterBold}>비고</Text></View>
          </View>

          <View style={styles.headerRow}>
            <View style={[styles.cellCenter, { width: '4%', backgroundColor: '#f8fafc' }]}></View>
            <View style={[styles.cellCenter, { width: '33%', backgroundColor: '#f8fafc' }]}></View>
            <View style={[styles.cellCenter, { width: '7%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenterBold}>수량</Text></View>
            <View style={[styles.cellCenter, { width: '14%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenterBold}>단가</Text></View>
            <View style={[styles.cellCenter, { width: '14%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenterBold}>계</Text></View>
            <View style={[styles.cellCenter, { width: '14%', backgroundColor: '#f8fafc' }]}></View>
            <View style={[styles.cellCenter, { width: '14%', borderRightWidth: 0, backgroundColor: '#f8fafc' }]}></View>
          </View>

          {computedItems.map((item, idx) => (
            <View key={idx} style={styles.dataRow}>
              <View style={[styles.cellCenter, { width: '4%' }]}><Text style={styles.textCenter}>{idx + 1}</Text></View>
              <View style={[styles.cellLeft, { width: '33%' }]}><Text style={styles.textLeft}>{item.taskName}</Text></View>
              <View style={[styles.cellCenter, { width: '7%' }]}><Text style={styles.textCenter}>{item.qty}</Text></View>
              <View style={[styles.cellRight, { width: '14%' }]}><Text style={styles.textRight}>{item.price.toLocaleString()}</Text></View>
              <View style={[styles.cellRight, { width: '14%' }]}><Text style={styles.textRight}>{(item.price * item.qty).toLocaleString()}</Text></View>
              <View style={[styles.cellRight, { width: '14%' }]}><Text style={styles.textRight}>{item.labor.toLocaleString()}</Text></View>
              <View style={[styles.cellCenter, { width: '14%', borderRightWidth: 0 }]}><Text style={styles.textCenter}>{item.note}</Text></View>
            </View>
          ))}

          {blankRows.map((_, idx) => (
            <View key={`blank-${idx}`} style={styles.dataRow}>
              <View style={[styles.cellCenter, { width: '4%' }]}><Text> </Text></View>
              <View style={[styles.cellLeft, { width: '33%' }]}><Text> </Text></View>
              <View style={[styles.cellCenter, { width: '7%' }]}><Text> </Text></View>
              <View style={[styles.cellRight, { width: '14%' }]}><Text> </Text></View>
              <View style={[styles.cellRight, { width: '14%' }]}><Text> </Text></View>
              <View style={[styles.cellRight, { width: '14%' }]}><Text> </Text></View>
              <View style={[styles.cellCenter, { width: '14%', borderRightWidth: 0 }]}><Text> </Text></View>
            </View>
          ))}

          {discountAmount > 0 ? (
            <>
              <View style={styles.dataRow}>
                <View style={[styles.cellCenter, { width: '37%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenterBold}>소계</Text></View>
                <View style={[styles.cellRight, { width: '21%' }]}><Text style={styles.textRightBold}>{computedSubTotal.toLocaleString()}</Text></View>
                <View style={[styles.cellCenter, { width: '14%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenterBold}>VAT</Text></View>
                <View style={[styles.cellRight, { width: '28%', borderRightWidth: 0 }]}><Text style={styles.textRightBold}>{computedVat.toLocaleString()}</Text></View>
              </View>
              <View style={styles.dataRow}>
                <View style={[styles.cellCenter, { width: '37%', backgroundColor: '#fef2f2' }]}><Text style={[styles.textCenterBold, { color: '#dc2626' }]}>할인</Text></View>
                <View style={[styles.cellRight, { width: '21%' }]}><Text style={[styles.textRightBold, { color: '#dc2626' }]}>-{discountAmount.toLocaleString()}</Text></View>
                <View style={[styles.cellCenter, { width: '14%', backgroundColor: '#f1f5f9' }]}><Text style={styles.textCenterBold}>합계</Text></View>
                <View style={[styles.cellRight, { width: '28%', borderRightWidth: 0, backgroundColor: '#f1f5f9' }]}><Text style={styles.textRightBold}>{computedTotal.toLocaleString()}</Text></View>
              </View>
            </>
          ) : (
            <View style={styles.dataRow}>
              <View style={[styles.cellCenter, { width: '37%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenterBold}>소계</Text></View>
              <View style={[styles.cellRight, { width: '21%' }]}><Text style={styles.textRightBold}>{computedSubTotal.toLocaleString()}</Text></View>
              <View style={[styles.cellCenter, { width: '14%', backgroundColor: '#f8fafc' }]}><Text style={styles.textCenterBold}>VAT</Text></View>
              <View style={[styles.cellRight, { width: '14%' }]}><Text style={styles.textRightBold}>{computedVat.toLocaleString()}</Text></View>
              <View style={[styles.cellCenter, { width: '14%', borderRightWidth: 0, backgroundColor: '#f1f5f9' }]}><Text style={styles.textRightBold}>합계 {computedTotal.toLocaleString()}</Text></View>
            </View>
          )}

          <View style={styles.legalSection}>
            <Text style={styles.legalText}>[자동차관리법] 제58조 제4항 및 같은 법 시행규칙 제134조 제2항에 따라 위와 같이 발급합니다.</Text>
            <Text style={styles.dateText}>{new Date().getFullYear()}년 {new Date().getMonth() + 1}월 {new Date().getDate()}일</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <Text style={{ fontSize: 11, fontWeight: 'bold' }}>주식회사 이투빌리티 (인)</Text>
              <View style={{ width: 30, height: 30, borderRadius: 15, borderWidth: 1, borderColor: '#ef4444', justifyContent: 'center', alignItems: 'center' }}>
                <Text style={{ fontSize: 9, color: '#ef4444' }}>직인</Text>
              </View>
            </View>
          </View>

          <View style={styles.footerBox}>
            <View style={styles.noticeList}>
              <Text style={{ fontWeight: 'bold', marginBottom: 4 }}>안내사항</Text>
              <Text>1. 정비업자가 점검·정비의 잘못으로 다음 구분에 따른 기간중 발생하는 고장 등에 대하여는 무상점검·정비를 합니다.</Text>
              <Text>   가. 차령 1년 미만 또는 주행거리 2만킬로미터 이내의 자동차: 점검·정비일부터 90일 이내</Text>
              <Text>   나. 차령 3년 미만 또는 주행거리 6만킬로미터 이내의 자동차: 점검·정비일부터 60일 이내</Text>
              <Text>   다. 차령 3년 이상 또는 주행거리 6만킬로미터 이상의 자동차: 점검·정비일부터 30일 이내</Text>
              <Text style={{ marginTop: 4 }}>2. 이 내역서는 2부를 작성, 정비의뢰자에게 1부를 교부하고, 정비사업자는 1부를 1년간 문서 또는 전산자료로 보관하여야 합니다.</Text>
              <Text style={{ marginTop: 6, color: '#000000' }}>※ 부품품질보증기간: 점검·정비일로부터 6개월 또는 10,000km 이내</Text>
            </View>
            <View style={styles.brandBox}>
              <Text style={styles.bankText}>IBK기업은행 377-139129-04-011</Text>
              <Text style={{ fontSize: 8, marginBottom: 8 }}>예금주: (주) 이투빌리티</Text>
              <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#16a34a', letterSpacing: -0.5 }}>E2Bility</Text>
              <Text style={{ fontSize: 5, color: '#16a34a', marginTop: 1 }}>Energy Everywhere</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default AsInvoicePdf;